import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";
import ButtonFlowList from "./ButtonFlowList";
import AdaptiveSelectCard from "./AdaptiveSelectCard";
import "./Scheduling/styles/Calendar.sass";
import {
  ScheduleConfirmationCard,
  TimeSlotsMenuSelection,
} from "./Scheduling/PcpSchedulingCard";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { RefillConfirmation } from "./RefillConfirmation";
import { Authentication, MFACode } from "./Authentication";
import { TierTwoButton } from "./TierTwoButton";
import { FormInputcard } from "./FormInput";
import SchedulingCard from "./SchedulingCard";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  CHAT_HTML_SANITIZE_CONFIG,
  getChatLinkTarget,
  getMarkedLinkData,
  linkifyEpicHttpUrls,
  rewriteSanitizedChatLinks,
  transformChatLinkUrl,
} from "../helpers/utils";

const transformChatUrl = (url, key, node) =>
  transformChatLinkUrl(url, key, node, defaultUrlTransform);

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: "8px 8px 8px 0px",
    background: "var(--CUI2-neutral-surface-container-highest, #F0F1F1)",
    marginRight: 42,
  },
  button_flow_list: {
    width: 296,
  },
  schedulingCard: {
    width: 285,
  },

  billing_refining_questions: {
    width: 296,
  },
  find_care_refining_questions: {
    width: 260,
  },
  chipsContent: {
    display: "flex",
    flexWrap: "wrap",
    padding: 16,
    rowGap: 8,
    columnGap: 4,
    fontSize: 12,
  },
  list_button: {
    borderRadius: "8px !important",
    height: "52px !important",
  },
  textContent: {
    fontSize: 14,
    padding: 12,
    fontWeight: 500,
    lineHeight: "125%",
    letterSpacing: "0.11px",

    "& a": {
      color: "var(--azure-chatbot-link-color, var(--Primary-Blue---500, #006298))",
      fontWeight: 500,
      textDecoration: "none",
      letterSpacing: "0.11px",

      "&:hover": {
        textDecoration: "underline",
      },

      "&:visited": {
        textDecoration: "none",
        color:
          "var(--azure-chatbot-link-visited-color, var(--Accent-B-Purple---500, #8B3C7C))",
      },
    },
  },
  markdownLink: {
    color: "var(--azure-chatbot-link-color, var(--Primary-Blue---500, #006298))",
    fontWeight: 500,
    textDecoration: "none",
    letterSpacing: "0.11px",

    "&:hover": {
      textDecoration: "underline",
    },

    "&:visited": {
      textDecoration: "none",
      color:
        "var(--azure-chatbot-link-visited-color, var(--Accent-B-Purple---500, #8B3C7C))",
    },
  },
  timeframe: {
    "&:not(:last-child)": {
      borderBottom: "1px solid #DAE2EA !important",
    },
  },
  actionStack: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "flex-start",
    "& .MuiButtonBase-root": {
      background: "none",
    },
  },
  actionTextContent: {
    whiteSpace: "pre-line",
  },
  actionButtonWrapper: {
    width: "calc(100% - 42px)",
  },
  actionTierTwoButton: {
    height: "44px !important",
    padding: "10px 24px 10px 16px !important",
    fontSize: "12px !important",
    lineHeight: "24px !important",
  },
  ageVerificationCard: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
}));

export default function CustomCard({
  card,
  next,
  handleChipClick,
  slots,
  minimizeBot,
  isAgeVerificationResolved,
}) {
  const classes = useStyles();
  const markdownRenderer = React.useMemo(() => {
    const renderer = new marked.Renderer();
    renderer.link = (...linkArguments) => {
      const { href, text, title } = getMarkedLinkData(...linkArguments);
      const safeHref = href ?? "";
      const titleAttr = title ? ` title="${title}"` : "";
      const target = getChatLinkTarget(safeHref);
      const relAttr =
        target === "_blank" ? ' rel="noopener noreferrer"' : "";
      return `<a class="${classes.markdownLink}" href="${safeHref}" target="${target}"${relAttr}${titleAttr}>${text}</a>`;
    };
    return renderer;
  }, [classes.markdownLink]);
  const markdownComponents = React.useMemo(
    () => ({
      p: ({ node, children, ...props }) => {
        if (typeof children === "string") {
          return (
            <p style={{ margin: 0 }} {...props}>
              {children}
            </p>
          );
        }
        return (
          <p style={{ margin: "16px 0" }} {...props}>
            {children}
          </p>
        );
      },
      a: ({ node, children, ...props }) => {
        const target = getChatLinkTarget(props.href);

        return (
          <a
            {...props}
            className={[props.className, classes.markdownLink]
              .filter(Boolean)
              .join(" ")}
            target={target}
            rel={target === "_blank" ? "noopener noreferrer" : undefined}
          >
            {children}
          </a>
        );
      },
    }),
    [classes.markdownLink]
  );

  const cardContent = (card) => {
    const content = card.attachment.content;
    const isOpenUrlAction = (action) =>
      action?.type?.toLowerCase() === "action.openurl";
    const openUrlAction =
      content?.actions?.find(isOpenUrlAction) ||
      content?.body
        ?.flatMap((item) => item?.actions || [])
        .find(isOpenUrlAction);
    const actionTextBlocks =
      content?.body?.filter(
        (item) => item?.type === "TextBlock" && item?.text
      ) || [];
    const actionText = actionTextBlocks.map((item) => item.text).join("\n\n");

    switch (content?.subtype) {

      case "button_flow_list":
      case "billing_refining_questions":
      case "find_care_refining_questions":
        return (
          <div
            className={`${classes.card} ${
              classes[card.attachment.content?.subtype]
            }`}
          >
            <ButtonFlowList
              actions={card.attachment.content.actions}
              handleChipClick={handleChipClick}
            />
          </div>
        );

      case "account_verification_options":
      case "yes_no":
      case "schedule_timeframe_selection":
      case "account_mfa_sendcode":
      case "tier_two_default_subtype":
      case "chat_language":
        return (
          <div className={classes.card}>
            <TierTwoButton
              isSignIn={
                card.attachment.content?.subtype ===
                "account_verification_options"
              }
              actions={card.attachment.content.actions}
              handleChipClick={handleChipClick}
              minimizeBot={minimizeBot}
            />
          </div>
        );
      case "age_verification":
        return (
          <div className={classes.ageVerificationCard}>
            {actionText && (
              <div
                className={`${classes.textContent} ${classes.card} ${classes.actionTextContent}`}
              >
                {actionText}
              </div>
            )}
          <div className={classes.card}>
            <TierTwoButton
              actions={card.attachment.content.actions}
              handleChipClick={handleChipClick}
              minimizeBot={minimizeBot}
              disabled={isAgeVerificationResolved}
              disableAfterClick
            />
          </div>
          </div>
        );
      case "account_sign_in":
        return (
          <div className={classes.card}>
            <Authentication
              content={card.attachment.content}
              handleClick={handleChipClick}
            />
          </div>
        );

      case "chat_name":
      case "chat_phone_zip":
      case "schedule_symptoms_request":
        return (
          <div className={classes.card}>
            <FormInputcard
              content={card.attachment.content}
              handleClick={handleChipClick}
              isRequired={
                card.attachment.content?.subtype === "schedule_symptoms_request"
                  ? true
                  : false
              }
            />
          </div>
        );

      case "schedule_location_selection":
      case "refill_medication_selection":
      case "refill_pharmacy_selection":
        return (
          <div className={classes.card}>
            <AdaptiveSelectCard
              content={card.attachment.content}
              handleClick={handleChipClick}
            />
          </div>
        );

      case "account_mfa_entercode":
        return (
          <div className={classes.card}>
            <MFACode
              content={card.attachment.content}
              handleClick={handleChipClick}
            />
          </div>
        );

      case "refill_confirmation":
        return (
          <div className={classes.card}>
            <RefillConfirmation
              content={card.attachment.content}
              handleClick={handleChipClick}
            />
          </div>
        );

      case "schedule_timeslot_menu_selection":
        return (
          <TimeSlotsMenuSelection
            content={card.attachment.content}
            handleClick={handleChipClick}
          />
        );

      case "schedule_timeslot_selection":
        return (
          <div className={`${classes.card} ${classes.schedulingCard}`}>
            <SchedulingCard slots={slots} handleClick={handleChipClick} />
          </div>
        );

      case "schedule_confirmation":
        return (
          <div className={classes.card}>
            <ScheduleConfirmationCard
              content={card.attachment.content}
              handleClick={handleChipClick}
            />
          </div>
        );
      case "surveyLink":
      case "open_new_tab_subtype": {
        return (
          <div className={classes.actionStack}>
            {actionText && (
              <div className={classes.card}>
                <div
                  className={`${classes.textContent} ${classes.actionTextContent}`}
                >
                  {actionText}
                </div>
              </div>
            )}
            <div className={classes.actionButtonWrapper}>
              <TierTwoButton
                actions={[openUrlAction]}
                handleChipClick={handleChipClick}
                minimizeBot={minimizeBot}
                buttonClassName={classes.actionTierTwoButton}
                useListButton={false}
              />
            </div>
          </div>
        );
      }

      default:
        return next(card);
    }
  };

  function MarkdownWithParagraphs(content) {
    return (
      <ReactMarkdown
        components={markdownComponents}
        remarkPlugins={[remarkGfm]}
        urlTransform={transformChatUrl}
      >
        {linkifyEpicHttpUrls(content)}
      </ReactMarkdown>
    );
  }

  return card?.attachment?.contentType ===
    "application/vnd.microsoft.card.adaptive" ? (
    cardContent(card)
  ) : (
    <>
      <div className={classes.card}>
        <div className={classes.textContent}>
          {card.attachment.contentType === "text/markdown" ? (
            MarkdownWithParagraphs(card.attachment.content)
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: rewriteSanitizedChatLinks(
                  DOMPurify.sanitize(
                    marked(linkifyEpicHttpUrls(card.attachment.content), {
                      renderer: markdownRenderer,
                    }),
                    CHAT_HTML_SANITIZE_CONFIG
                  )
                ),
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}

CustomCard.propTypes = {
  directLine: PropTypes.object,
  card: PropTypes.object,
};
